export const DEFAULT_CAPITAL_REQUEST_LABEL = "Capital Request";
export const MAX_CAPITAL_REQUEST_LABEL_LENGTH = 20;

/**
 * Custom label from Misc Settings (capital_request_label) applies only to the NEW CR flow:
 * cr-recommendations tab, Recommendations isCapitalRequest, Origin source, dashboard new-CR entry points.
 *
 * Legacy capital-request module (capitalrequest tab, /capital-request/, /project-capital-request/)
 * always uses DEFAULT_CAPITAL_REQUEST_LABEL ("Capital Request") and must not call getCapitalRequestLabel.
 */

export const isValidCapitalRequestLabel = value => {
    const trimmed = (value || "").trim();
    return trimmed.length >= 1 && trimmed.length <= MAX_CAPITAL_REQUEST_LABEL_LENGTH;
};

export const getCapitalRequestLabel = miscellaneous =>
    (miscellaneous?.capital_request_label || "").trim() || DEFAULT_CAPITAL_REQUEST_LABEL;

/** New CR recommendations flow only — excludes legacy capitalrequest tab URLs. */
const NEW_CR_QUICK_LINK_URL = /cr-recommendations|capital-requests|is_cr=true/i;

/** Display-only: true when a bookmark/action URL targets the new CR recommendations flow. */
export const isCapitalRequestQuickLinkUrl = url => NEW_CR_QUICK_LINK_URL.test(url || "");

export const pluralizeLabel = label => (label.endsWith("s") ? label : `${label}s`);

const normalizeQuickLinkUrl = url => (url || "").replace(/^\//, "");

const isCrRecommendationsTabUrl = url => /\/cr-recommendations/i.test(normalizeQuickLinkUrl(url));

/** True when stored name still uses canonical "Capital Request" phrasing (not a pencil rename to arbitrary text). */
const containsCanonicalCrPhrase = name => {
    if (name == null || name === "") return false;
    const str = String(name);
    return (
        str.includes(DEFAULT_CAPITAL_REQUEST_LABEL) ||
        str.includes(pluralizeLabel(DEFAULT_CAPITAL_REQUEST_LABEL))
    );
};

/**
 * Display-only: Quick Links / bookmark label using current project misc settings.
 * Persists canonical names in DB via getQuickLinkStoredName.
 */
export const getQuickLinkDisplayName = (item, miscellaneous) => {
    if (!item?.name) return item?.name;
    const url = item.url || "";
    if (!isCapitalRequestQuickLinkUrl(url)) return item.name;

    if (!containsCanonicalCrPhrase(item.name)) {
        if (isCrRecommendationsTabUrl(url)) {
            return getCapitalRequestLabel(miscellaneous);
        }
        return item.name;
    }

    if (isCrRecommendationsTabUrl(url)) {
        return getCapitalRequestLabel(miscellaneous);
    }

    return formatCapitalRequestDisplayText(item.name, miscellaneous);
};

/**
 * Canonical name to persist when creating a bookmark (not the misc display label).
 */
export const getQuickLinkStoredName = (displayName, url) => {
    if (!isCapitalRequestQuickLinkUrl(url)) return displayName;

    if (isCrRecommendationsTabUrl(url)) {
        return DEFAULT_CAPITAL_REQUEST_LABEL;
    }

    const normalized = normalizeQuickLinkUrl(url);
    if (/capital-requests/i.test(normalized)) {
        const str = String(displayName || "");
        if (/^add\s+/i.test(str)) {
            return `Add ${DEFAULT_CAPITAL_REQUEST_LABEL}`;
        }
    }

    if (containsCanonicalCrPhrase(displayName)) {
        return formatCapitalRequestDisplayText(displayName, null);
    }

    return displayName;
};

/** Display-only: rewrite "Capital Request(s)" in UI strings using Misc Settings label. */
export const formatCapitalRequestDisplayText = (text, miscellaneous) => {
    if (text == null || text === "") return text;
    const str = String(text);
    const label = getCapitalRequestLabel(miscellaneous);
    if (label === DEFAULT_CAPITAL_REQUEST_LABEL) return str;
    const plural = pluralizeLabel(label);
    return str.replace(/\bCapital Requests\b/g, plural).replace(/\bCapital Request\b/g, label);
};

/** Display-only: map persisted origin value "Capital Request" to project Misc Settings label. */
export const formatCapitalRequestSourceValue = (value, miscellaneous) => {
    if (value == null || value === "") return value;
    const str = String(value);
    if (str === DEFAULT_CAPITAL_REQUEST_LABEL) {
        return getCapitalRequestLabel(miscellaneous);
    }
    return value;
};

export const getCapitalRequestNarrativeLabel = misc => `${getCapitalRequestLabel(misc)} Narrative`;

export const getCapitalRequestStatusLabel = misc => `${getCapitalRequestLabel(misc)} Status`;

export const getCapitalRequestStatusNotesLabel = misc => `${getCapitalRequestLabel(misc)} Status Notes`;

/** Display-only: requestor filter option on CR recommendations table. */
export const getMyRequestedCapitalRequestFilterLabel = miscellaneous =>
    formatCapitalRequestDisplayText("My requested Capital Request", miscellaneous);
